import React, { useState } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";
import { ArrowRight, Mail, CheckCircle2, Send } from "lucide-react";

const SUPPORT_EMAIL = "create@divergentcinema.co.uk";

const empty = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-contact">
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        <div className="absolute inset-0 halftone-pink opacity-[0.08]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Get in touch</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Say hello.<br />
            <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">We&apos;ll write back.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#2C2C2C] leading-relaxed">
            Submissions, partnerships, accessibility on set, press, or just a friendly hello — drop us a line.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Direct email */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-[#FFD93D] border-2 border-[#0A0A0A] pop-shadow p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0A0A0A] mb-3">Email us directly</div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                data-testid="contact-direct-email"
                className="font-heading font-black text-[#0A0A0A] text-xl md:text-2xl break-all inline-flex items-center gap-2 hover:text-[#FF2E63] transition-colors"
              >
                <Mail className="w-5 h-5 flex-shrink-0" /> {SUPPORT_EMAIL}
              </a>
              <p className="mt-4 text-sm text-[#0A0A0A] leading-relaxed">
                We aim to reply within 5 working days. Plain English, no jargon, no forms — we promise.
              </p>
            </div>

            <div className="bg-white border-2 border-[#0A0A0A] p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">What to write about</div>
              <ul className="text-sm text-[#2C2C2C] space-y-2">
                <li>· Submitting a film to the programme</li>
                <li>· Joining the professional directory</li>
                <li>· Partnerships, venues, funding</li>
                <li>· Press and interviews</li>
                <li>· Accessibility questions or feedback</li>
              </ul>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <div className="bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow p-10" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 mb-4" />
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD93D] mb-3">Message received</div>
                <h2 className="font-heading font-black text-3xl tracking-tight leading-tight">Thanks — we&apos;ll be in touch.</h2>
                <p className="mt-3 text-[#FFE8A8] leading-relaxed">We reply within 5 working days. If it&apos;s urgent, email <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
                <button
                  onClick={() => { setDone(false); setForm(empty); }}
                  data-testid="contact-send-another"
                  className="mt-6 bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
                >
                  Send another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border-2 border-[#0A0A0A] pop-shadow p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                <Field label="Your name" required>
                  <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
                </Field>
                <Field label="Email" required>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className={inputCls} />
                </Field>
                <Field label="Subject" required full>
                  <input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} required className={inputCls} />
                </Field>
                <Field label="Message" required full>
                  <textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} required className={inputCls} />
                </Field>

                {error && (
                  <div className="md:col-span-2 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm" data-testid="contact-error" role="alert">{error}</div>
                )}

                <div className="md:col-span-2">
                  <button type="submit" disabled={submitting} data-testid="contact-submit" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-8 py-4 text-base font-medium inline-flex items-center gap-2">
                    <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none";

function Field({ label, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A]">
        {label} {required && <span className="text-[#FF2E63]">*</span>}
      </span>
      {children}
    </label>
  );
}
